import { msg } from "@lingui/core/macro";
import { type Component, type JSX, Show } from "solid-js";
import { useI18n } from "@/providers/i18n-provider";
import { EmptyState } from "./empty-state";
import { Button } from "./button";

type NotFoundVariant = "not-found" | "no-access" | "offline";

interface NotFoundStateProps {
  variant?: NotFoundVariant;
  /** Título y descripción a medida (sobreescriben el copy por defecto). */
  title?: string;
  description?: string;
  /** Acción primaria (p.ej. volver al inicio / a la pantalla padre). */
  primaryAction?: { label: string; onClick: () => void };
  /** Acción secundaria opcional (p.ej. reintentar). */
  secondaryAction?: { label: string; onClick: () => void };
  class?: string;
  "data-testid"?: string;
}

/**
 * Ilustración gastronómica (theme-aware) por variante. Reutiliza el set de
 * `EmptyState` para mantener una identidad visual consistente en lugar de
 * iconos genéricos.
 */
const ILLUSTRATIONS: Record<NotFoundVariant, "plate" | "menu" | "tables"> = {
  "not-found": "plate",
  "no-access": "menu",
  offline: "tables",
};

/**
 * Estado de "no encontrado / sin acceso / sin conexión" para rutas y vistas de
 * detalle cuya entidad no se pudo resolver (ver `createEntityResolution`).
 * Reutiliza el `EmptyState` premium (ilustración brand-aware, fade-in bajo el
 * guard global de reduced-motion) y ofrece copy localizado por defecto +
 * acciones claras, en lugar de dejar la pantalla cargando indefinidamente o
 * mostrando un vacío engañoso.
 */
export const NotFoundState: Component<NotFoundStateProps> = (props) => {
  const { t } = useI18n();
  const variant = (): NotFoundVariant => props.variant ?? "not-found";

  const defaultTitle = (): string => {
    if (variant() === "no-access") {
      return t(msg`You don't have access`);
    }
    if (variant() === "offline") {
      return t(msg`Can't load this yet`);
    }
    return t(msg`Not found`);
  };

  const defaultDescription = (): string => {
    if (variant() === "no-access") {
      return t(msg`This item doesn't exist or you don't have permission to view it.`);
    }
    if (variant() === "offline") {
      return t(
        msg`You're offline and this hasn't synced to this device yet. Reconnect and try again.`,
      );
    }
    return t(msg`This item may have been deleted, or the link is no longer valid.`);
  };

  const action = (): JSX.Element => (
    <div class="flex flex-col items-center gap-2 sm:flex-row">
      <Show when={props.primaryAction}>
        {(primary) => (
          <Button
            onClick={() => {
              primary().onClick();
            }}
          >
            {primary().label}
          </Button>
        )}
      </Show>
      <Show when={props.secondaryAction}>
        {(secondary) => (
          <Button
            variant="outline"
            onClick={() => {
              secondary().onClick();
            }}
          >
            {secondary().label}
          </Button>
        )}
      </Show>
    </div>
  );

  // La ilustración reenvía la acción primaria (o la secundaria si no hay
  // primaria) para que tocar el ícono dispare el CTA, igual que el botón.
  const iconAction = (): { label: string; onClick: () => void } | undefined =>
    props.primaryAction ?? props.secondaryAction;

  return (
    <EmptyState
      data-testid={props["data-testid"]}
      illustration={ILLUSTRATIONS[variant()]}
      title={props.title ?? defaultTitle()}
      description={props.description ?? defaultDescription()}
      action={(props.primaryAction ?? props.secondaryAction) ? action() : undefined}
      onIconActivate={iconAction() ? () => iconAction()?.onClick() : undefined}
      iconActionLabel={iconAction()?.label}
      class={props.class}
    />
  );
};
